import React from "react";

import Form from "components/Form";
import Panel from "components/Panel";
import PanelSection from "components/PanelSection";
import { useRoutes } from "utils/routes";
import ServerLocalCollectorSettings from "components/ServerLocalCollectorSettings";

import { CollectorSettingsQuery } from "./gql/Query.generated";

type Props = {
  server: CollectorSettingsQuery["getServerDetails"];
  permittedToEditServer: boolean;
};

const EditForm = ({ server, permittedToEditServer }: Props) => {
  const { server: serverRoute } = useRoutes();
  if (!permittedToEditServer) {
    return (
      <Panel title="Collector Settings">
        <PanelSection>
          You are not allowed to edit this server - you need the{" "}
          <strong>Modify</strong> permission on the server.
        </PanelSection>
      </Panel>
    );
  }

  const {
    humanId,
    statementResetFrequency,
    statementTimeoutMs,
    statementTimeoutMsQueryText,
    statementMaxSizeMb,
    usesLocalCollector,
  } = server;

  return (
    <Panel title="Collector Settings">
      <Form action={serverRoute(humanId)} method="patch">
        <div className="form-group">
          <label
            className="control-label"
            htmlFor="server_collector_features_statement_reset_frequency"
          >
            Maximum Query Stats Reset Frequency
          </label>
          <div className="row">
            <div className="col-sm-5">
              <select
                className="form-control"
                defaultValue={String(statementResetFrequency)}
                name="server[collector_features][statement_reset_frequency]"
                id="server_collector_features_statement_reset_frequency"
              >
                <option value="">None - reset disabled</option>
                <option value="6">Every 1 hour</option>
                <option value="36">Every 6 hours</option>
                <option value="72">Every 12 hours</option>
                <option value="144">Every 24 hours</option>
              </select>
            </div>
            <div className="col-sm-7">
              <div className="help-block">
                Enables{" "}
                <a
                  target="_blank"
                  href="https://pganalyze.com/docs/install/troubleshooting/reset_stat_statements"
                >
                  automatic pg_stat_statements resets
                </a>{" "}
                when 90% full to avoid "query text unavailable". Requires
                collector version 0.70.0 or later.
              </div>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label
            className="control-label"
            htmlFor="server_collector_features_statement_max_size_mb"
          >
            Maximum Query Size Before Next Reset
          </label>
          <div className="row">
            <div className="col-sm-5">
              <select
                className="form-control"
                defaultValue={String(statementMaxSizeMb)}
                name="server[collector_features][statement_max_size_mb]"
                id="server_collector_features_statement_max_size_mb"
              >
                <option value="">Default (250 MB)</option>
                <option value="250">250 MB</option>
                <option value="500">500 MB</option>
                <option value="1000">1 GB</option>
                <option value="2000">2 GB</option>
              </select>
            </div>
            <div className="col-sm-7">
              <div className="help-block">
                When reset frequency is enabled, this also resets if the total
                combined size of all query text becomes too large.
              </div>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label
            className="control-label"
            htmlFor="server_collector_features_statement_timeout_ms"
          >
            Statement Timeout
          </label>
          <div className="row">
            <div className="col-sm-5">
              <select
                className="form-control"
                defaultValue={String(statementTimeoutMs)}
                name="server[collector_features][statement_timeout_ms]"
                id="server_collector_features_statement_timeout_ms"
              >
                <option value="30000">30s</option>
                <option value="60000">60s</option>
                <option value="300000">300s</option>
              </select>
            </div>
            <div className="col-sm-7">
              <div className="help-block">
                Timeout for all statements the collector runs, except query text
                statements.
              </div>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label
            className="control-label"
            htmlFor="server_collector_features_statement_timeout_ms_query_text"
          >
            Query Text Timeout
          </label>
          <div className="row">
            <div className="col-sm-5">
              <select
                className="form-control"
                defaultValue={String(statementTimeoutMsQueryText)}
                name="server[collector_features][statement_timeout_ms_query_text]"
                id="server_collector_features_statement_timeout_ms_query_text"
              >
                <option value="120000">120s</option>
                <option value="300000">300s</option>
                <option value="600000">600s</option>
              </select>
            </div>
            <div className="col-sm-7">
              <div className="help-block">
                Timeout for statements that retrieve query text from
                pg_stat_statements.
              </div>
            </div>
          </div>
        </div>
        {usesLocalCollector && (
          <>
            <hr />
            <ServerLocalCollectorSettings
              server={server}
              systemType={server.systemType}
            />
          </>
        )}
        <div className="form-group">
          <input
            type="submit"
            name="commit"
            value="Save Settings"
            className="btn btn-success"
            data-disable-with="Save Settings"
          />
        </div>
      </Form>
    </Panel>
  );
};

export default EditForm;
